import {
  Badge,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputBase,
  Menu,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  createStyles,
  fade,
  makeStyles,
  Theme,
  withStyles
} from '@material-ui/core/styles';
import {
  AddRounded,
  CloseRounded,
  DeleteRounded,
  EditRounded,
  HistoryRounded,
  KeyboardArrowDownRounded,
  KeyboardArrowLeftRounded,
  KeyboardArrowRightRounded,
  MoreVertRounded,
  SearchRounded,
  UndoRounded,
  UnfoldLessRounded,
  UnfoldMoreRounded
} from '@material-ui/icons';
import {
  Alert,
  Autocomplete,
  Pagination,
  ToggleButton,
  ToggleButtonGroup
} from '@material-ui/lab';
import { KeyboardDatePicker } from '../../../components/CustomDatePicker';
import clsx from 'clsx';
import moment from 'moment';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Redirect, Link as RRLink, useHistory } from 'react-router-dom';
import { DisciplineLabel, PurposeLabel, RevisionLabel } from '.';
import {
  GetBulkPlanningDocumentParams,
  GetPipingBulkPlanningDocumentParams
} from '../../../api/documents';
import { DeletePipingProjectDetailPipingListParams } from '../../../api/piping-project-detail-piping-lists';
import { UpdatePipingProjectDetail } from '../../../api/piping-project-details';
import {
  BulkProjectDocument,
  CreateBulkPipingProjectDocument
} from '../../../api/project-documents';
import { RootState } from '../../../app/store';
import { ProductNameLabel } from '../../../common/enum/ProductNameLabel';
import {
  BulkDocument,
  BulkDocumentRevision,
  DocumentRevision,
  HistoryDeleteDocumentRevision
} from '../../../common/interface/bulkPlanning';
import { PaginationProps } from '../../../common/interface/pagination';
import ConfirmDeleteBox from '../../../components/ConfirmDeleteBox';
import HelpTooltip from '../../../components/HelpTooltip';
import { DAYS_FOR_COMPLETE_REVIEW } from '../../../constants';
import { addWeekdays } from '../../../functions/addWeekdays';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import { useQueryStringNumber } from '../../../hooks/useQueryString';
import AutoAwesomeRounded from '../../../icon/AutoAwesomeRounded';
import ContentCopyOutlined from '../../../icon/ContentCopyOutlined';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import {
  Document,
  getBulkPlanningDocumentsAsync,
  getPipingBulkPlanningDocumentsAsync
} from '../../MasterData/Document/documentSlice';
import {
  getPipingListGroupsAsync,
  PipingList
} from '../../MasterData/PipingListGroup/pipingListGroupSlice';
import {
  getPipingModelTypesAsync,
  PipingModelType
} from '../../MasterData/PipingModelType/pipingModelTypeSlice';
import {
  getPipingPlantTypesAsync,
  PipingPlantType
} from '../../MasterData/PipingPlantType/pipingPlantTypeSlice';
import {
  getPipingWorkTypesAsync,
  PipingWorkType
} from '../../MasterData/PipingWorkType/pipingWorkTypeSlice';
import {
  getPipingProjectDetailAsync,
  updatePipingProjectDetailAsync
} from '../../PipingProjectDetail/pipingProjectDetailSlice';
import { deletePipingProjectDetailPipingListAsync } from '../../PipingProjectDetailPipingList/pipingProjectDetailPipingListSlice';
import { getProjectAsync } from '../../Project/projectSlice';
import { User } from '../../UserProfile/userProfileSlice';
import { checkProductType } from '../DocumentCreate';
import {
  bulkCreatePipingProjectDocumentsAsync,
  getAllProjectDocumentNosAsync
} from '../projectDocumentSlice';

enum PipingWorkTypeLabel {
  EPC = 1,
  FEED = 2
}

interface ProjectInformation {
  pipingWorkType: PipingWorkType | null;
  pipingPlantType: PipingPlantType | null;
  pipingModelType: PipingModelType | null;
  pipingLists: PipingList[];
}

type ViewMode = 'expand' | 'collapse';

// Steps will use translation function inside component
const stepKeys = [
  'addProjectScope',
  'selectDocuments',
  'customizeTemplate',
  'createDocuments'
];

export default function PipingBulkPlanning() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const projectId = useQueryStringNumber({ query: 'projectId' });

  const { selectedProject } = useSelector((state: RootState) => state.projects);
  const { allProjectDocumentNos } = useSelector(
    (state: RootState) => state.projectDocuments
  );
  const { selectedPipingProjectDetail } = useSelector(
    (state: RootState) => state.pipingProjectDetails
  );
  const { bulkPlanning: templateDocuments, pipingTemplateDocuments } =
    useSelector((state: RootState) => state.documents);
  const { data: pipingWorkTypes } = useSelector(
    (state: RootState) => state.pipingWorkTypes
  );
  const { data: pipingPlantTypes } = useSelector(
    (state: RootState) => state.pipingPlantTypes
  );
  const { data: pipingModelTypes } = useSelector(
    (state: RootState) => state.pipingModelTypes
  );
  const { data: pipingListGroups } = useSelector(
    (state: RootState) => state.pipingListGroups
  );
  const { data: purposes } = useSelector((state: RootState) => state.purposes);
  const { data: revisions } = useSelector(
    (state: RootState) => state.revisions
  );
  const { data: accessControls } = useSelector(
    (state: RootState) => state.accessControls
  );
  const { data: documents } = useSelector(
    (state: RootState) => state.documents
  );
  const { users } = useSelector((state: RootState) => state.projectRegisters);
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.userProfile
  );

  const [selectedPipingListIds, setSelectedPipingListIds] = useState<number[]>(
    []
  );
  const [
    selectedPipingTemplateDocumentIds,
    setSelectedPipingTemplateDocumentIds
  ] = useState<number[]>([]);
  const [selectedDocumentRefIds, setSelectedDocumentRefIds] = useState<
    number[]
  >([]);

  const [projectInformation, setProjectInformation] =
    useState<ProjectInformation>({
      pipingWorkType: null,
      pipingPlantType: null,
      pipingModelType: null,
      pipingLists: []
    });
  const needUpdateProjectInformation =
    !selectedPipingProjectDetail ||
    selectedPipingProjectDetail?.pipingWorkTypeId !=
    projectInformation.pipingWorkType?.id ||
    selectedPipingProjectDetail?.pipingPlantTypeId !=
    projectInformation.pipingPlantType?.id ||
    selectedPipingProjectDetail?.pipingModelTypeId !=
    projectInformation.pipingModelType?.id;
  const selectedFeedWorkType = Boolean(
    projectInformation.pipingWorkType?.id === PipingWorkTypeLabel.FEED
  );
  const [selectedPipingList, setSelectedPipingList] =
    useState<PipingList | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<BulkDocument | null>(
    null
  );
  const [params, setParams] = useState<{ search: string | null }>({
    search: null
  });
  const [code, setCode] = useState<{
    plant: string | null;
    area: string | null;
  }>({
    plant: null,
    area: null
  });
  const [pagination, setPagination] = useState<PaginationProps>({
    page: 0,
    limit: 30
  });
  const defaultDocumentRevisions: DocumentRevision[] = [
    { purposeId: PurposeLabel.IFR, revisionId: RevisionLabel.A },
    { purposeId: PurposeLabel.IFC, revisionId: RevisionLabel.Zero },
    { purposeId: PurposeLabel.IFF, revisionId: RevisionLabel.One }
  ];
  const [documentRevisions, setDocumentRevisions] = useState<
    DocumentRevision[]
  >(defaultDocumentRevisions);
  const [bulkDocuments, setBulkDocuments] = useState<BulkDocument[]>([]);
  const [historyDeleteDocumentRevisions, setHistoryDeleteDocumentRevisions] =
    useState<HistoryDeleteDocumentRevision[]>([]);

  const [activeStep, setActiveStep] = useState(0);
  const [openedPipingListGroupIds, setOpenedPipingListGroupIds] = useState<
    number[]
  >([]);
  const [openedDocumentRefIds, setOpenedDocumentRefIds] = useState<number[]>(
    []
  );
  const [isEditRevision, setIsEditRevision] = useState<boolean>(false);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [isShowAllUsers, setIsShowAllUsers] = useState<boolean>(false);
  const [openDeletePipingListDialog, setOpenDeletePipingListDialog] =
    useState<boolean>(false);
  const [openDeleteOneDialog, setOpenDeleteOneDialog] =
    useState<boolean>(false);
  const [openDeleteAllDialog, setOpenDeleteAllDialog] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const originalNo = 'XXX-000-PIP-CAL-0001-00';

  useEffect(() => {
    // Prepare the document in the `Create Documents` step only
    if (selectedProject && activeStep === 3) {
      const projectDocuments = templateDocuments.map((document, index) => {
        let userIds: number[] = [];
        document.projectPositionWorkflowSteps.forEach(
          (projectPositionWorkflowStep) => {
            if (projectPositionWorkflowStep.projectPosition) {
              projectPositionWorkflowStep.projectPosition.userProjectPositions.forEach(
                (userProjectPosition) =>
                  userIds.push(userProjectPosition.userId)
              );
            }
          }
        );
        // Filter array to have unique values
        userIds = userIds.filter((item, pos) => {
          return userIds.indexOf(item) == pos;
        });

        let documentCreatorId = null;
        // Select document creator from logged in user. If logged in user isn't document creator list, the system will select the first user
        if (userIds.length > 0) {
          documentCreatorId =
            userIds.find((userId) => userId === loggedInUser?.id) ?? userIds[0];
        }

        const projectDocument: BulkDocument = {
          refId: index, // To prevent remove wrong record when deleting document after selecting
          name: document.name,
          no: document.no,
          accessControlId: 2,
          documentCreatorId,
          disciplineId: document.disciplineId,
          documentId: document.id,
          isAIMS: false,
          projectId: selectedProject.id,
          projectDocumentRevisions: documentRevisions.map(
            ({ purposeId, revisionId }, index) => {
              return {
                refId: index, // To record order of revision using for undo action
                planIssueDate: null,
                planCompletedDate: null,
                manHour: 1,
                purposeId,
                revisionId,
                isAutoApproval: purposeId === PurposeLabel.IFR
              };
            }
          ),
          userIds
        };

        // Magic for selecting REPCO as:
        const repcoId = 7;
        switch (checkProductType(selectedProject.productName)) {
          // 1. Consultant on PMC project
          case ProductNameLabel.pmc:
            projectDocument.consultantId = repcoId;
            break;
          // 2. Main contractor on EPC project
          case ProductNameLabel.epc:
            projectDocument.contractorId = repcoId;
            break;
          default:
            break;
        }

        return projectDocument;
      });
      setBulkDocuments(projectDocuments);
      setOpenedDocumentRefIds([0]);
    }
  }, [templateDocuments, activeStep]);

  useEffect(() => {
    const pipingWorkType =
      pipingWorkTypes.find(
        ({ id }) => id === selectedPipingProjectDetail?.pipingWorkTypeId
      ) ?? null;
    const pipingPlantType =
      pipingPlantTypes.find(
        ({ id }) => id === selectedPipingProjectDetail?.pipingPlantTypeId
      ) ?? null;
    const pipingModelType =
      pipingModelTypes.find(
        ({ id }) => id === selectedPipingProjectDetail?.pipingModelTypeId
      ) ?? null;
    const pipingLists = selectedPipingProjectDetail?.pipingLists ?? [];
    setProjectInformation({
      pipingWorkType,
      pipingPlantType,
      pipingModelType,
      pipingLists
    });
  }, [
    selectedPipingProjectDetail,
    pipingWorkTypes,
    pipingPlantTypes,
    pipingModelTypes
  ]);

  useEffect(() => {
    if (pipingListGroups[0]) {
      setOpenedPipingListGroupIds([pipingListGroups[0].id]);
    }
  }, [pipingListGroups]);

  useEffect(() => {
    if (projectId) {
      dispatch(getPipingProjectDetailAsync(projectId));
    }
  }, [projectId]);

  useEffect(() => {
    if (pipingWorkTypes.length === 0) {
      dispatch(getPipingWorkTypesAsync());
    }
    if (pipingPlantTypes.length === 0) {
      dispatch(getPipingPlantTypesAsync());
    }
    if (pipingModelTypes.length === 0) {
      dispatch(getPipingModelTypesAsync());
    }
    if (pipingListGroups.length === 0) {
      dispatch(getPipingListGroupsAsync());
    }
    if (projectId) {
      dispatch(getProjectAsync(projectId));
    }
  }, []);

  function handleChangeSearch(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    const value = event.target.value;
    const search = value === '' ? null : value;
    setParams({ ...params, search });
  }

  function filteredDocuments(documents: Document[]): Document[] {
    return documents.filter((document) => {
      // Check if the document matches the search value
      const matchesSearch =
        !params.search ||
        document.name.toLowerCase().includes(params.search.toLowerCase()) ||
        document.no.toLowerCase().includes(params.search.toLowerCase());

      // Check if the document id exists in the selectedPipingTemplateDocumentIds
      const isSelected = selectedPipingTemplateDocumentIds.includes(
        document.id
      );

      // Return true if both conditions are met
      return matchesSearch || isSelected;
    });
  }

  function renderBulkDocumentByPage(
    bulkDocuments: BulkDocument[],
    pagination: PaginationProps
  ): BulkDocument[] {
    const startIndex = pagination.page * pagination.limit; // Calculate the starting index
    const endIndex = startIndex + pagination.limit; // Calculate the ending index

    // Use slice to return the documents for the current page
    return bulkDocuments.slice(startIndex, endIndex);
  }

  function checkDocumentNo(document: BulkDocument): {
    error: boolean;
    helperText: string;
  } {
    // Ensure document.no is not null before proceeding
    if (!document.no) {
      return {
        error: true,
        helperText: t('bulkPlanning.piping.validation.cannotBeBlank') as string
      };
    }

    // Check if the document number already exists in allProjectDocumentNos
    const isDuplicateCreatedDocument = allProjectDocumentNos.includes(
      document.no
    );
    if (isDuplicateCreatedDocument) {
      return {
        error: true,
        helperText: t('bulkPlanning.piping.validation.numberInUse') as string
      };
    }

    // Check if the document number already exists in bulkDocuments (excluding the current document)
    const isDuplicateIncomingDocument = bulkDocuments.some(
      (bulkDocument) =>
        bulkDocument !== document && bulkDocument.no === document.no
    );
    if (isDuplicateIncomingDocument) {
      return {
        error: true,
        helperText: t('bulkPlanning.piping.validation.duplicateNumber') as string
      };
    }

    // If no duplicates are found
    return { error: false, helperText: '' };
  }

  function modifiedNo(originalNo: string): string {
    let arrayFromOriginalNo = originalNo.split('-');
    // Replace plant name (at Position 0, Remove 1 item, and add `plant`)
    if (code.plant) arrayFromOriginalNo.splice(0, 1, code.plant.toUpperCase());
    // Replace area number (at Position 1, Remove 1 item, and add `area`)
    if (code.area) arrayFromOriginalNo.splice(1, 1, code.area.toUpperCase());
    // @See more: https://www.w3schools.com/jsref/jsref_splice.asp
    return arrayFromOriginalNo.join('-');
  }

  function generateRevisionChip(
    { purposeId, revisionId }: DocumentRevision,
    key: number
  ): ReactNode {
    const purpose = purposes.find((purpose) => purpose.id === purposeId);
    const revision = revisions.find((revision) => revision.id === revisionId);

    return (
      <React.Fragment key={key}>
        <Tooltip
          title={`${t('bulkPlanning.tableHeaders.purpose')}: ${purpose?.fullName} | ${t('bulkPlanning.tableHeaders.revision')}: ${revision?.name}`}
          arrow
        >
          <Chip
            className={classes.truncatedChip}
            variant="outlined"
            label={`${t('bulkPlanning.tableHeaders.purpose')}: ${purpose?.abbreviation} | ${t('bulkPlanning.tableHeaders.revision')}: ${revision?.name}`}
          />
        </Tooltip>
      </React.Fragment>
    );
  }

  function nextButton(step: number): { disabled: boolean; action: any } {
    switch (step) {
      case 0:
        return {
          disabled:
            needUpdateProjectInformation ||
            !Boolean(projectInformation.pipingWorkType?.id) ||
            loading,
          action: async () => {
            try {
              setLoading(true);

              if (projectInformation.pipingWorkType) {
                const params: GetPipingBulkPlanningDocumentParams = {
                  pipingWorkTypeId: projectInformation.pipingWorkType.id,
                  pipingPlantTypeId:
                    projectInformation.pipingPlantType?.id ?? null,
                  pipingModelTypeId:
                    projectInformation.pipingModelType?.id ?? null,
                  pipingListIds: selectedPipingListIds
                };
                await dispatch(
                  getPipingBulkPlanningDocumentsAsync(params)
                ).unwrap();
                setSelectedPipingTemplateDocumentIds([]);
                setTimeout(() => {
                  handleNext();
                  setLoading(false);
                }, 250);
              } else {
                throw new Error(t('bulkPlanning.piping.validation.selectWorkType') as string);
              }
            } catch (error) {
              setLoading(false);
              if (error instanceof Error)
                dispatch(
                  loadingSlice.actions.error({
                    message: error.message
                  })
                );
            }
          }
        };
      case 1:
        return {
          disabled: selectedPipingTemplateDocumentIds.length === 0 || loading,
          action: handleNext
        };
      case 2:
        return {
          disabled: isEditRevision || loading,
          action: async () => {
            try {
              if (projectId) {
                const params: GetBulkPlanningDocumentParams = {
                  projectId,
                  disciplineId: DisciplineLabel.PIP,
                  ids: selectedPipingTemplateDocumentIds,
                  plant: code.plant,
                  area: code.area
                };
                await dispatch(getBulkPlanningDocumentsAsync(params)).unwrap();
                dispatch(
                  getAllProjectDocumentNosAsync({
                    projectId,
                    disciplineIds: [DisciplineLabel.PIP]
                  })
                );
                setSelectedDocumentRefIds([]);
                setHistoryDeleteDocumentRevisions([]);
                setPagination({ ...pagination, page: 0 });
                setTimeout(() => {
                  handleNext();
                  setLoading(false);
                }, 250);
              }
            } catch {
              setLoading(false);
            }
          }
        };
      case 3:
        return {
          disabled:
            bulkDocuments.length === 0 ||
            bulkDocuments.some(
              (document) =>
                !document.name ||
                !document.no ||
                !document.accessControlId ||
                !document.documentCreatorId ||
                document.projectDocumentRevisions.some(
                  (documentRevision) =>
                    !documentRevision.purposeId ||
                    !documentRevision.revisionId ||
                    !documentRevision.manHour ||
                    !documentRevision.planIssueDate ||
                    !documentRevision.planCompletedDate
                )
            ) ||
            bulkDocuments.some(
              (bulkDocument) =>
                bulkDocument.no &&
                allProjectDocumentNos.includes(bulkDocument.no)
            ) ||
            bulkDocuments.some((bulkDocument) =>
              bulkDocuments
                .filter((document) => document.refId !== bulkDocument.refId)
                .some((document) => document.no === bulkDocument.no)
            ) ||
            loading,
          action: handleNext // The action in this step doesn't use, we will use `onSubmit` function instead
        };
      default:
        return { disabled: true, action: handleNext };
    }
  }

  function handleNext(): void {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  }

  function handleBack(): void {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  }

  function handleChangePage(
    event: React.ChangeEvent<unknown>,
    value: number
  ): void {
    setPagination({ ...pagination, page: value - 1 }); // The first value of pagination is 1, not 0
    setSelectedDocumentRefIds([]);
    if (pagination.page !== value - 1)
      window['scrollTo']({ top: 0, behavior: 'smooth' });
  }

  function handleEditRevision(): void {
    setIsEditRevision((prevEdit) => !prevEdit);
  }

  function handleChangePipingView(
    event: React.MouseEvent<HTMLElement>,
    newValue: ViewMode
  ) {
    if (newValue === 'expand') {
      const allItems = pipingListGroups.map(
        (pipingListGroup) => pipingListGroup.id
      );
      setOpenedPipingListGroupIds(allItems);
    } else if (newValue === 'collapse') {
      setOpenedPipingListGroupIds([]);
    }
  }

  function getToggleStatus(
    selectedItems: number,
    allItems: number
  ): ViewMode | null {
    if (selectedItems === 0) return 'collapse';
    if (selectedItems === allItems) return 'expand';
    return null;
  }

  function handleOpenPipingListGroup(id: number): void {
    const open = getStatus(id, openedPipingListGroupIds);
    if (open) {
      setOpenedPipingListGroupIds(
        openedPipingListGroupIds.filter(
          (openedPipingListGroupId) => openedPipingListGroupId !== id
        )
      );
    } else {
      setOpenedPipingListGroupIds([...openedPipingListGroupIds, id]);
    }
  }

  function handleSelectPipingList(id: number): void {
    const selected = getStatus(id, selectedPipingListIds);
    if (selected) {
      setSelectedPipingListIds(
        selectedPipingListIds.filter(
          (selectedPipingListId) => selectedPipingListId !== id
        )
      );
    } else {
      setSelectedPipingListIds([...selectedPipingListIds, id]);
    }
  }

  function handleChangeProjectInformation<K extends keyof ProjectInformation>(
    value: ProjectInformation[K],
    field: K
  ): void {
    const updatedProjectInformation = { ...projectInformation, [field]: value };

    function isPipingWorkType(value: any): value is PipingWorkType {
      return value !== null && typeof value === 'object' && 'id' in value;
    }

    if (field === 'pipingWorkType' && isPipingWorkType(value)) {
      if (value.id === PipingWorkTypeLabel.FEED) {
        updatedProjectInformation.pipingPlantType = null;
        updatedProjectInformation.pipingModelType = null;
      }
    }

    setProjectInformation(updatedProjectInformation);
  }

  function handleOpenDocument(refId: number): void {
    const open = getStatus(refId, openedDocumentRefIds);
    if (open) {
      // If the document is already open, remove it from the list
      setOpenedDocumentRefIds(
        openedDocumentRefIds.filter(
          (openedDocumentRefId) => openedDocumentRefId !== refId
        )
      );
    } else {
      // Add the document to the list, ensuring a maximum of 5 documents
      setOpenedDocumentRefIds((prevRefIds) => {
        const updatedRefs = [...prevRefIds, refId];
        return updatedRefs.length > 5 ? updatedRefs.slice(1) : updatedRefs;
      });
    }
  }

  function handleSelectPipingTemplateDocument(
    checked: boolean,
    id?: number
  ): void {
    if (id) {
      // Click from each document
      if (checked) {
        setSelectedPipingTemplateDocumentIds([
          ...selectedPipingTemplateDocumentIds,
          id
        ]);
      } else {
        setSelectedPipingTemplateDocumentIds(
          selectedPipingTemplateDocumentIds.filter(
            (documentId) => documentId !== id
          )
        );
      }
    } else {
      // Click from header of piping template document table
      if (checked) {
        const allDocumentIds = pipingTemplateDocuments.map(({ id }) => id);
        setSelectedPipingTemplateDocumentIds(allDocumentIds);
      } else {
        setSelectedPipingTemplateDocumentIds([]);
      }
    }
  }

  function handleSelectDocument(checked: boolean, refId?: number) {
    if (refId !== undefined) {
      // Click from each document
      if (checked) {
        setSelectedDocumentRefIds([...selectedDocumentRefIds, refId]);
      } else {
        setSelectedDocumentRefIds(
          selectedDocumentRefIds.filter(
            (documentRefId) => documentRefId !== refId
          )
        );
      }
    } else {
      // Click from header of document table
      if (checked) {
        const allDocumentRefIds = renderBulkDocumentByPage(
          bulkDocuments,
          pagination
        ).map(({ refId }) => refId);
        setSelectedDocumentRefIds(allDocumentRefIds);
      } else {
        setSelectedDocumentRefIds([]);
      }
    }
  }

  function getStatus(value: number, selectedItems: number[]): boolean {
    return selectedItems.indexOf(value) !== -1;
  }

  function getValue<T extends { id: number }>(
    id: number | null,
    options: T[]
  ): T | null {
    return options.find((option) => option.id === id) ?? null;
  }

  function handleChangeTextField(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    const { name, value } = event.target;
    setCode({
      ...code,
      [name]: value === '' ? null : value.toUpperCase().substring(0, 3)
    });
  }

  function handleChangeRevisionTemplate<K extends keyof DocumentRevision>(
    value: DocumentRevision[K],
    field: K,
    index: number
  ): void {
    const documentRevision = documentRevisions[index];

    // Create updated revision object
    const updatedDocumentRevision = {
      ...documentRevision,
      [field]: value ?? null
    };

    // {t('bulkPlanning.actions.reset')} `revisionId` if `purposeId` is changed
    if (field === 'purposeId') {
      updatedDocumentRevision.revisionId = null;
    }

    // Update the specific revision in place
    documentRevisions.splice(index, 1, updatedDocumentRevision);
    setDocumentRevisions([...documentRevisions]);
  }

  function handleRemoveRevisionTemplate(index: number): void {
    // Remove the revision at the specified index
    documentRevisions.splice(index, 1);
    setDocumentRevisions([...documentRevisions]);
  }

  function handleNewRevisionTemplate(): void {
    setDocumentRevisions([
      ...documentRevisions,
      { purposeId: null, revisionId: null }
    ]);
  }

  function handleResetRevisionTemplate(): void {
    setDocumentRevisions(defaultDocumentRevisions);
  }

  function handleAnchorElement(
    open: boolean
  ): (event: React.MouseEvent<HTMLButtonElement>) => void {
    return function (event: React.MouseEvent<HTMLButtonElement>) {
      setAnchorElement(open ? event.currentTarget : null);
    };
  }

  function getRelatedUser(userIds: number[], users: User[]): User[] {
    return !isShowAllUsers
      ? users.filter((user) => userIds.some((userId) => userId === user.id))
      : users;
  }

  function handleChangeRelatedUsers(): void {
    setIsShowAllUsers((prev) => !prev);
  }

  function handleDuplicateDocument(document: BulkDocument): void {
    // Find the maximum `redId` in `bulkDocuments`
    const maxRefId = Math.max(
      ...bulkDocuments.map((document) => document.refId)
    );

    // Create a new document by duplicating the selected document and assigning a new `refId`
    const newDocument = {
      ...document,
      refId: maxRefId + 1, // New `refId`
      projectDocumentRevisions: document.projectDocumentRevisions.map(
        (revision) => ({
          ...revision
        })
      )
    };

    // Find the index of the selected document in `bulkDocuments`
    const documentIndex = bulkDocuments.findIndex(
      (doc) => doc.refId === document.refId
    );

    // Add the new document next to the selected document
    bulkDocuments.splice(documentIndex + 1, 0, newDocument);

    // Check if the new document is on a new page
    const lastDocumentIndexInThisPage =
      pagination.page * pagination.limit + pagination.limit - 1;
    const isNewPage = documentIndex + 1 > lastDocumentIndexInThisPage;
    const newPage = isNewPage ? pagination.page + 1 : pagination.page;

    setPagination({ ...pagination, page: newPage });
    setBulkDocuments([...bulkDocuments]);

    if (isNewPage) window['scrollTo']({ top: 0, behavior: 'smooth' });
  }

  function handleChangeDocumentDetail<K extends keyof BulkDocument>(
    value: BulkDocument[K],
    field: K,
    refId: number
  ): void {
    const documentIndex = bulkDocuments.findIndex(
      (document) => document.refId === refId
    );
    if (documentIndex === -1) return;
    const document = bulkDocuments[documentIndex];

    const newDocument = { ...document, [field]: value === '' ? null : value };

    bulkDocuments.splice(documentIndex, 1, newDocument);
    setBulkDocuments([...bulkDocuments]);
  }

  function handleChangeDocumentRevisionDetail<
    K extends keyof BulkDocumentRevision
  >(
    value: BulkDocumentRevision[K],
    field: K,
    refId: number,
    documentRevisionIndex: number,
    options?: { positiveNumber: boolean }
  ): void {
    const documentIndex = bulkDocuments.findIndex(
      (document) => document.refId === refId
    );
    if (documentIndex === -1) return;
    const document = bulkDocuments[documentIndex];
    const documentRevision =
      document.projectDocumentRevisions[documentRevisionIndex];

    const newDocumentRevision = {
      ...documentRevision,
      [field]:
        options?.positiveNumber && typeof value === 'number'
          ? value <= 0
            ? null
            : value
          : value
    };
    if (field === 'purposeId') {
      newDocumentRevision.revisionId = null;
      newDocumentRevision.isAutoApproval = value === PurposeLabel.IFR;
    }
    if (field === 'planIssueDate') {
      if (value instanceof Date) {
        newDocumentRevision.planCompletedDate = addWeekdays(
          value,
          DAYS_FOR_COMPLETE_REVIEW
        );
      } else {
        newDocumentRevision.planCompletedDate = null;
      }
    }

    document.projectDocumentRevisions.splice(
      documentRevisionIndex,
      1,
      newDocumentRevision
    );
    bulkDocuments.splice(documentIndex, 1, {
      ...document,
      projectDocumentRevisions: document.projectDocumentRevisions
    });
    setBulkDocuments([...bulkDocuments]);
  }

  function handleOpenDeletePipingListDialog(pipingList?: PipingList): void {
    setOpenDeletePipingListDialog((prev) => !prev);
    if (pipingList) setSelectedPipingList(pipingList);
  }

  function handleOpenDeleteOneDialog(document?: BulkDocument): void {
    setOpenDeleteOneDialog((prev) => !prev);
    if (document) setSelectedDocument(document);
  }

  function handleOpenDeleteAllDialog(): void {
    setOpenDeleteAllDialog((prev) => !prev);
  }

  async function onReset(): Promise<void> {
    if (projectId) {
      const params: GetBulkPlanningDocumentParams = {
        projectId,
        disciplineId: DisciplineLabel.PIP,
        ids: selectedPipingTemplateDocumentIds,
        plant: code.plant,
        area: code.area
      };
      await dispatch(getBulkPlanningDocumentsAsync(params)).unwrap();
      setPagination({ ...pagination, page: 0 });
    }
  }

  async function onDeletePipingList(): Promise<void> {
    if (projectId && selectedPipingList) {
      const params: DeletePipingProjectDetailPipingListParams = {
        projectId,
        pipingListId: selectedPipingList.id
      };
      await dispatch(deletePipingProjectDetailPipingListAsync(params)).unwrap();
      dispatch(getPipingProjectDetailAsync(projectId));
      setOpenDeletePipingListDialog(false);
    }
  }

  function onDeleteDocument(): void {
    if (selectedDocument) {
      const newDocument = bulkDocuments.filter(
        (document) => document.refId !== selectedDocument.refId
      );
      const newOpenedDocumentRefIds = openedDocumentRefIds.filter(
        (refId) => selectedDocument.refId !== refId
      );
      const newHistoryDeleteDocumentRevisions =
        historyDeleteDocumentRevisions.filter(
          ({ refDocumentId }) => selectedDocument.refId !== refDocumentId
        );
      const newSelectedDocumentRefIds = selectedDocumentRefIds.filter(
        (refId) => selectedDocument.refId !== refId
      );
      setBulkDocuments(newDocument);
      setOpenedDocumentRefIds(newOpenedDocumentRefIds);
      setHistoryDeleteDocumentRevisions(newHistoryDeleteDocumentRevisions);
      setSelectedDocumentRefIds(newSelectedDocumentRefIds);

      // Navigate to previous page if the deleted document is the last row
      const isLastRow =
        renderBulkDocumentByPage(bulkDocuments, pagination).length === 1;
      const newPage = Math.max(
        isLastRow ? pagination.page - 1 : pagination.page,
        0
      );
      setPagination({ ...pagination, page: newPage });
      if (isLastRow) window['scrollTo']({ top: 0, behavior: 'smooth' });

      setOpenDeleteOneDialog(false);
    }
  }

  function onDeleteSelectedDocuments(): void {
    const newDocument = bulkDocuments.filter((document) =>
      selectedDocumentRefIds.every((refId) => refId !== document.refId)
    );
    const newOpenedDocumentRefIds = openedDocumentRefIds.filter((id) =>
      selectedDocumentRefIds.every((refId) => refId !== id)
    );
    const newHistoryDeleteDocumentRevisions =
      historyDeleteDocumentRevisions.filter(({ refDocumentId }) =>
        selectedDocumentRefIds.every((refId) => refId !== refDocumentId)
      );
    setBulkDocuments(newDocument);
    setOpenedDocumentRefIds(newOpenedDocumentRefIds);
    setHistoryDeleteDocumentRevisions(newHistoryDeleteDocumentRevisions);
    setSelectedDocumentRefIds([]);

    // Navigate to previous page if the all document is deleted
    const newPage = Math.max(pagination.page - 1, 0);
    setPagination({ ...pagination, page: newPage });
    window['scrollTo']({ top: 0, behavior: 'smooth' });

    setOpenDeleteAllDialog(false);
  }

  function onDeleteDocumentRevision(
    document: BulkDocument,
    documentRevision: BulkDocumentRevision
  ): void {
    const documentIndex = bulkDocuments.findIndex(
      ({ refId }) => refId === document.refId
    );
    if (documentIndex >= 0) {
      // Collect deleted revision to history called `historyDeleteDocumentRevisions`
      const historyDocumentRevision: HistoryDeleteDocumentRevision = {
        refDocumentId: document.refId,
        projectDocumentRevision: documentRevision
      };
      setHistoryDeleteDocumentRevisions([
        historyDocumentRevision,
        ...historyDeleteDocumentRevisions
      ]);
      // Remove the revision from selected projectDocumentRevisions
      const newDocumentRevisions = document.projectDocumentRevisions.filter(
        ({ refId }) => refId !== documentRevision.refId
      );

      bulkDocuments[documentIndex] = {
        ...document,
        projectDocumentRevisions: newDocumentRevisions
      };
      setBulkDocuments([...bulkDocuments]);
    }
  }

  function onUndoDocumentRevision(document?: BulkDocument): void {
    if (document) {
      const history = historyDeleteDocumentRevisions.find(
        ({ refDocumentId }) => refDocumentId === document.refId
      );

      if (history) {
        const { projectDocumentRevision } = history;
        const documentIndex = bulkDocuments.findIndex(
          ({ refId }) => refId === document.refId
        );

        if (documentIndex >= 0) {
          // Restore the latest deleted document revision
          const newDocumentRevisions = [
            ...document.projectDocumentRevisions,
            projectDocumentRevision
          ].sort((a, b) => a.refId - b.refId);

          bulkDocuments[documentIndex] = {
            ...document,
            projectDocumentRevisions: newDocumentRevisions
          };
          setBulkDocuments([...bulkDocuments]);

          // Remove from history
          setHistoryDeleteDocumentRevisions(
            historyDeleteDocumentRevisions.filter(
              (history) =>
                !(
                  history.refDocumentId === document.refId &&
                  history.projectDocumentRevision.refId ===
                  projectDocumentRevision.refId
                )
            )
          );
        }
      }
    } else {
      const latestDeleteDocumentRevision = historyDeleteDocumentRevisions[0];
      if (latestDeleteDocumentRevision) {
        const { refDocumentId } = latestDeleteDocumentRevision;

        const document = bulkDocuments.find(
          ({ refId }) => refId === refDocumentId
        );

        if (document) {
          onUndoDocumentRevision(document);
        }
      }
    }
  }

  async function updateProjectInformation(): Promise<void> {
    try {
      if (projectId && projectInformation.pipingWorkType) {
        const updatePipingProjectDetail: UpdatePipingProjectDetail = {
          projectId,
          pipingProjectDetail: {
            pipingWorkTypeId: projectInformation.pipingWorkType.id,
            pipingPlantTypeId: projectInformation.pipingPlantType?.id ?? null,
            pipingModelTypeId: projectInformation.pipingModelType?.id ?? null
          }
        };
        await dispatch(
          updatePipingProjectDetailAsync(updatePipingProjectDetail)
        ).unwrap();
        dispatch(
          loadingSlice.actions.success({
            message: t('bulkPlanning.piping.messages.projectInfoUpdated') as string
          })
        );
      } else {
        throw new Error(t('bulkPlanning.piping.errors.selectWorkType') as string);
      }
    } catch (error) {
      setLoading(false);
      if (error instanceof Error)
        dispatch(
          loadingSlice.actions.error({
            message: error.message
          })
        );
    }
  }

  async function onSubmit(
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();

    // Add condition to check the value of bulkDocuments
    const isValidBulkDocuments = bulkDocuments.every((document) => {
      return (
        document.name &&
        document.no &&
        document.accessControlId &&
        document.documentCreatorId &&
        document.disciplineId &&
        document.documentId &&
        document.projectId &&
        document.projectDocumentRevisions.every((revision) => {
          return (
            revision.planIssueDate &&
            revision.planCompletedDate &&
            revision.manHour &&
            revision.purposeId &&
            revision.revisionId
          );
        })
      );
    });

    try {
      setLoading(true);
      if (projectId && isValidBulkDocuments) {
        const createdProjectDocument: CreateBulkPipingProjectDocument = {
          projectDocuments: bulkDocuments as BulkProjectDocument[],
          pipingProjectDetail: {
            projectId,
            pipingListIds: selectedPipingListIds
          }
        };
        await dispatch(
          bulkCreatePipingProjectDocumentsAsync(createdProjectDocument)
        ).unwrap();
        dispatch(
          loadingSlice.actions.success({
            message: t('bulkPlanning.common.documentsCreatedSuccess')
          })
        );
        handleNext();
        setTimeout(() => {
          history.push(`/projects/${projectId}`);
        }, 1000);
      }
    } catch (error) {
      setLoading(false);
      if (error instanceof Error)
        dispatch(
          loadingSlice.actions.error({
            message: error.message
          })
        );
    }
  }

  if (!projectId) {
    return <Redirect to="/projects" />;
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="createBulkPlanning"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id,
          createBulkPlanning: 'PIP'
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('bulkPlanning.titles.PIP')}
      </Typography>
      <Stepper
        className={classes.card}
        component={Paper}
        activeStep={activeStep}
        alternativeLabel
      >
        {stepKeys.map((stepKey) => (
          <Step key={stepKey}>
            <StepLabel>{t(`bulkPlanning.piping.steps.${stepKey}`)}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <form onSubmit={onSubmit}>
        <Paper className={classes.card}>
          <Collapse in={activeStep === 0}>
            <div className={classes.flexSpace}>
              <div className={classes.flexGrow}>
                <Typography variant="h6" display="inline">
                  {t('bulkPlanning.labels.projectScope')}
                </Typography>
              </div>
              {selectedPipingProjectDetail?.pipingWorkTypeId && (
                <Button
                  component={RRLink}
                  to={`/documents/bulk-planning/pip/documents?projectId=${projectId}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                  disabled={needUpdateProjectInformation}
                >
                  {t('bulkPlanning.actions.viewRelatedDocuments')}
                </Button>
              )}
            </div>
            <Divider className={classes.divider} />
            <Typography>{t('bulkPlanning.labels.projectInformation')}</Typography>
            <div className={classes.my1}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    id="piping-work-type"
                    options={pipingWorkTypes}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('bulkPlanning.piping.labels.workType')}
                        variant="outlined"
                        size="small"
                        required={activeStep === 0}
                      />
                    )}
                    value={projectInformation.pipingWorkType}
                    onChange={(event, newValue) =>
                      handleChangeProjectInformation(newValue, 'pipingWorkType')
                    }
                  />
                </Grid>
                {!selectedFeedWorkType && (
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      id="piping-plant-type"
                      options={pipingPlantTypes}
                      getOptionLabel={(option) => option.name}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('bulkPlanning.piping.labels.plantType')}
                          variant="outlined"
                          size="small"
                        />
                      )}
                      value={projectInformation.pipingPlantType}
                      onChange={(event, newValue) =>
                        handleChangeProjectInformation(
                          newValue,
                          'pipingPlantType'
                        )
                      }
                      disabled={selectedFeedWorkType}
                    />
                  </Grid>
                )}
                {!selectedFeedWorkType && (
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      id="piping-model-type"
                      options={pipingModelTypes}
                      getOptionLabel={(option) => option.name}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('bulkPlanning.piping.labels.modelType')}
                          variant="outlined"
                          size="small"
                        />
                      )}
                      value={projectInformation.pipingModelType}
                      onChange={(event, newValue) =>
                        handleChangeProjectInformation(
                          newValue,
                          'pipingModelType'
                        )
                      }
                      disabled={selectedFeedWorkType}
                    />
                  </Grid>
                )}
              </Grid>
              <Collapse in={needUpdateProjectInformation}>
                <Button
                  className={classes.my2}
                  variant="contained"
                  color="primary"
                  startIcon={<HistoryRounded />}
                  fullWidth
                  onClick={updateProjectInformation}
                >
                  {t('bulkPlanning.actions.updateInformation')}
                </Button>
              </Collapse>
            </div>
            <Typography>{t('bulkPlanning.labels.existingInputInformation')}</Typography>
            {projectInformation.pipingLists.length > 0 ? (
              <ol>
                {projectInformation.pipingLists.map((pipingList, index) => {
                  return (
                    <li key={index}>
                      <div className={classes.flexSpace}>
                        <Typography className={classes.flexGrow}>
                          {pipingList.name}
                        </Typography>
                        <Tooltip
                          title={
                            needUpdateProjectInformation
                              ? (t('bulkPlanning.common.cannotDelete') as string) : (t('bulkPlanning.actions.delete') as string)
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOpenDeletePipingListDialog(pipingList)
                              }
                              disabled={needUpdateProjectInformation}
                            >
                              <DeleteRounded />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <div className={classes.fileNotFound}>
                <Typography
                  component="div"
                  variant="caption"
                  color="textSecondary"
                >
                  {t('bulkPlanning.labels.noInputInformation')}
                </Typography>
              </div>
            )}
            <Divider className={classes.my2} />
            <div className={classes.flexSpace}>
              <div className={classes.flexGrow}>
                <Typography>
                  {selectedPipingListIds.length > 0
                    ? `${selectedPipingListIds.length} ${t('bulkPlanning.labels.selected')}`
                    : t('bulkPlanning.labels.selectNewInputInformation')}
                </Typography>
              </div>
              <ToggleButtonGroup
                size="small"
                value={getToggleStatus(
                  openedPipingListGroupIds.length,
                  pipingListGroups.length
                )}
                exclusive
                onChange={handleChangePipingView}
              >
                <ToggleButton value="expand">
                  <UnfoldMoreRounded fontSize="small" />
                </ToggleButton>
                <ToggleButton value="collapse">
                  <UnfoldLessRounded fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
            <div className={classes.mt1}>
              <TableContainer style={{ overflowY: 'hidden' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="none" width={40} />
                      <TableCell align="right" width={64}>
                        {t('bulkPlanning.tableHeaders.item')}
                      </TableCell>
                      <TableCell>{t('bulkPlanning.tableHeaders.pipingDescription')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pipingListGroups.map((pipingListGroup, index) => {
                      const count = pipingListGroup.pipingLists.filter(
                        (pipingList) =>
                          selectedPipingListIds.includes(pipingList.id)
                      ).length;
                      const open = getStatus(
                        pipingListGroup.id,
                        openedPipingListGroupIds
                      );

                      return (
                        <React.Fragment key={index}>
                          <TableRow
                            className={clsx(
                              classes.clickable,
                              classes.unsetAll
                            )}
                            hover
                            onClick={() =>
                              handleOpenPipingListGroup(pipingListGroup.id)
                            }
                          >
                            <TableCell align="center" padding="none">
                              <Tooltip title={(open ? t('bulkPlanning.common.collapse') : t('bulkPlanning.common.expand')) as string}>
                                <IconButton size="small">
                                  {open ? (
                                    <KeyboardArrowDownRounded />
                                  ) : (
                                    <KeyboardArrowRightRounded />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">{index + 1}</TableCell>
                            <StyledBadge color="secondary" badgeContent={count}>
                              <TableCell className={classes.unset}>
                                {pipingListGroup.name}
                              </TableCell>
                            </StyledBadge>
                          </TableRow>
                          <TableRow>
                            <TableCell padding="none" colSpan={3}>
                              <Collapse in={open}>
                                <Table size="small">
                                  <TableBody>
                                    {pipingListGroup.pipingLists.map(
                                      (pipingList, index) => {
                                        return (
                                          <TableRow
                                            className={clsx(
                                              classes.clickable,
                                              classes.unsetAll
                                            )}
                                            hover
                                            selected={getStatus(
                                              pipingList.id,
                                              selectedPipingListIds
                                            )}
                                            onClick={() =>
                                              handleSelectPipingList(
                                                pipingList.id
                                              )
                                            }
                                            key={index}
                                          >
                                            <TableCell width={52} />
                                            <TableCell padding="checkbox">
                                              <Checkbox
                                                checked={getStatus(
                                                  pipingList.id,
                                                  selectedPipingListIds
                                                )}
                                              />
                                            </TableCell>
                                            <TableCell>
                                              {pipingList.name}
                                              <Typography
                                                display="block"
                                                variant="caption"
                                                color="textSecondary"
                                              >
                                                {pipingList.description}
                                              </Typography>
                                            </TableCell>
                                          </TableRow>
                                        );
                                      }
                                    )}
                                  </TableBody>
                                </Table>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </Collapse>
          <Collapse in={activeStep === 1}>
            <div className={classes.flexSpace}>
              <div className={classes.flexGrow}>
                <Typography variant="h6" display="inline">
                  {t('bulkPlanning.listOfDocuments')}
                </Typography>
                {selectedPipingTemplateDocumentIds.length > 0 ? (
                  <Typography display="inline">
                    {' '}
                    ({selectedPipingTemplateDocumentIds.length} {t('bulkPlanning.labels.selected')})
                  </Typography>
                ) : (
                  <Typography display="inline">
                    {' '}
                    ({pipingTemplateDocuments.length} {t('common.result')}
                    {pipingTemplateDocuments.length > 1 && ` ${t('common.results')}`})
                  </Typography>
                )}
              </div>
              <Paper className={classes.paperSearch} variant="outlined">
                <SearchRounded className={classes.colorTextSecondary} />
                <InputBase
                  placeholder={t('bulkPlanning.search') as string}
                  className={classes.searchInput}
                  type="search"
                  spellCheck={false}
                  fullWidth
                  value={params.search === null ? '' : params.search}
                  onChange={handleChangeSearch}
                  inputProps={{ 'aria-label': 'search' }}
                />
              </Paper>
            </div>
            <Divider className={classes.divider} />
            {pipingTemplateDocuments.length > 0 ? (
              <TableContainer style={{ overflowY: 'hidden' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          indeterminate={
                            selectedPipingTemplateDocumentIds.length > 0 &&
                            selectedPipingTemplateDocumentIds.length <
                            pipingTemplateDocuments.length
                          }
                          checked={
                            selectedPipingTemplateDocumentIds.length > 0 &&
                            selectedPipingTemplateDocumentIds.length ===
                            pipingTemplateDocuments.length
                          }
                          onChange={(event) =>
                            handleSelectPipingTemplateDocument(
                              event.target.checked
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>{t('bulkPlanning.tableHeaders.documentNo')}</TableCell>
                      <TableCell>{t('bulkPlanning.tableHeaders.documentName')}</TableCell>
                    </TableRow>
                  </TableHead>
                  {filteredDocuments(pipingTemplateDocuments).length > 0 ? (
                    filteredDocuments(pipingTemplateDocuments).map(
                      (document, index) => {
                        const selected = getStatus(
                          document.id,
                          selectedPipingTemplateDocumentIds
                        );
                        return (
                          <React.Fragment key={index}>
                            <TableRow
                              classes={{
                                root: clsx(
                                  classes.clickable,
                                  classes.unsetAll,
                                  {
                                    [classes.tableRowRoot]: selected
                                  }
                                ),
                                selected: clsx({
                                  [classes.tableRowSelected]: selected
                                })
                              }}
                              hover
                              selected={selected}
                              onClick={() =>
                                handleSelectPipingTemplateDocument(
                                  !selected,
                                  document.id
                                )
                              }
                            >
                              <TableCell align="center" padding="checkbox">
                                <Checkbox color="primary" checked={selected} />
                              </TableCell>
                              <TableCell>{document.no}</TableCell>
                              <TableCell>{document.name}</TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      }
                    )
                  ) : (
                    <TableRow>
                      <TableCell className={classes.fileNotFound} colSpan={3}>
                        {Boolean(params.search) ? (
                          <div>
                            {t('bulkPlanning.searchDidNotMatch', { search: params.search as string })}
                          </div>
                        ) : (
                          <div>{t('bulkPlanning.noDocumentTemplateFound')}</div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </Table>
              </TableContainer>
            ) : (
              <div className={classes.fileNotFound}>
                <Typography
                  component="div"
                  variant="caption"
                  color="textSecondary"
                >
                  {t('bulkPlanning.noDocumentTemplateFound')}
                </Typography>
              </div>
            )}
          </Collapse>
          <Collapse in={activeStep === 2}>
            <Typography variant="h6">{t('bulkPlanning.labels.documentNo')}</Typography>
            <Divider className={classes.divider} />
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Alert severity="info" icon={<AutoAwesomeRounded />}>
                  {t('bulkPlanning.common.systemUpdateDocuments')}
                </Alert>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('bulkPlanning.plantCode') as string}
                  variant="outlined"
                  size="small"
                  name="plant"
                  fullWidth
                  value={code.plant ? code.plant : ''}
                  onChange={handleChangeTextField}
                  placeholder={t('bulkPlanning.maxCharacters') as string}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('bulkPlanning.areaCode') as string}
                  variant="outlined"
                  size="small"
                  name="area"
                  fullWidth
                  value={code.area ? code.area : ''}
                  onChange={handleChangeTextField}
                  placeholder={t('bulkPlanning.maxCharacters') as string}
                />
              </Grid>
              {(code.plant || code.area) && (
                <Grid item xs={12}>
                  <Typography className={classes.px1} variant="caption">
                    {t('bulkPlanning.common.exampleDocumentNo', { originalNo, modifiedNo: modifiedNo(originalNo) })}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Divider className={classes.my2} />
            <div className={classes.header}>
              <Typography variant="h6">{t('bulkPlanning.labels.revisions')}</Typography>
              {!isEditRevision && (
                <Tooltip title={t('bulkPlanning.edit') as string} arrow>
                  <IconButton size="small" onClick={handleEditRevision}>
                    <EditRounded />
                  </IconButton>
                </Tooltip>
              )}
              {isEditRevision && (
                <Tooltip title={t('bulkPlanning.newRevision') as string} arrow>
                  <IconButton size="small" onClick={handleNewRevisionTemplate}>
                    <AddRounded />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <Divider className={classes.divider} />
            <Grid container spacing={1}>
              {!isEditRevision && (
                <Grid className={classes.revisionContainer} item xs={12}>
                  {documentRevisions.map((documentRevision, index) =>
                    generateRevisionChip(documentRevision, index)
                  )}
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography className={classes.px1} variant="caption">
                  <b>{t('bulkPlanning.labels.definition')}:</b>{' '}
                  {purposes
                    .map(
                      (purpose) =>
                        `${purpose.abbreviation}: ${purpose.fullName}`
                    )
                    .join(', ')}
                </Typography>
              </Grid>
              {isEditRevision && (
                <>
                  {documentRevisions.map((documentRevision, index) => {
                    const purpose = getValue(
                      documentRevision.purposeId,
                      purposes
                    );
                    const revisionList = purpose?.revisions ?? [];
                    const revision = getValue(
                      documentRevision.revisionId,
                      revisionList
                    );

                    return (
                      <Grid
                        className={classes.flexSpace}
                        item
                        xs={12}
                        key={index}
                      >
                        <Autocomplete
                          className={classes.flexGrow2}
                          id="purpose"
                          options={purposes}
                          getOptionLabel={(option) =>
                            `${option.abbreviation}: ${option.fullName}`
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t('bulkPlanning.tableHeaders.purpose')}
                              variant="outlined"
                              size="small"
                              required
                            />
                          )}
                          value={purpose}
                          onChange={(event, newValue) =>
                            handleChangeRevisionTemplate(
                              newValue?.id ?? null,
                              'purposeId',
                              index
                            )
                          }
                        />
                        <div
                          className={clsx(classes.flexSpace, classes.flexGrow)}
                        >
                          <Autocomplete
                            className={classes.flexGrow}
                            id="revision"
                            options={revisionList}
                            getOptionLabel={(option) => option.name}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('bulkPlanning.labels.revision')}
                                variant="outlined"
                                size="small"
                                required
                              />
                            )}
                            value={revision}
                            onChange={(event, newValue) =>
                              handleChangeRevisionTemplate(
                                newValue?.id ?? null,
                                'revisionId',
                                index
                              )
                            }
                          />
                          <div>
                            <Tooltip
                              title={
                                documentRevisions.length <= 1
                                  ? (t('bulkPlanning.common.cannotRemove') as string) : (t('bulkPlanning.common.remove') as string)
                              }
                              arrow
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={documentRevisions.length <= 1}
                                  onClick={() =>
                                    handleRemoveRevisionTemplate(index)
                                  }
                                >
                                  <CloseRounded />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </div>
                        </div>
                      </Grid>
                    );
                  })}
                  <Grid item xs={12} sm={6}>
                    <Button
                      color="primary"
                      variant="outlined"
                      fullWidth
                      onClick={handleResetRevisionTemplate}
                    >
                      {t('bulkPlanning.actions.reset')}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      color="primary"
                      variant="contained"
                      disableElevation
                      fullWidth
                      disabled={documentRevisions.some(
                        ({ purposeId, revisionId }) => !purposeId || !revisionId
                      )}
                      onClick={handleEditRevision}
                    >
                      {t('bulkPlanning.actions.update')}
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Collapse>
          <Collapse in={activeStep >= 3}>
            <div className={classes.flexSpace}>
              <Typography variant="h6" display="inline">
                {t('bulkPlanning.listOfDocuments')}
              </Typography>
              {bulkDocuments.length > 0 &&
                selectedDocumentRefIds.length === 0 && (
                  <Typography display="inline">
                    {' '}
                    ({bulkDocuments.length} {t('common.result')}
                    {bulkDocuments.length > 1 && ` ${t('common.results')}`})
                  </Typography>
                )}
              {selectedDocumentRefIds.length > 0 && (
                <Typography display="inline">
                  {' '}
                  ({selectedDocumentRefIds.length} {t('bulkPlanning.labels.selected')})
                </Typography>
              )}
            </div>
            <Divider className={classes.divider} />
            {activeStep >= 3 && // To improve the performance, This part will be rendered on it's step only
              bulkDocuments.length > 0 ? (
              <>
                <TableContainer style={{ overflowY: 'hidden' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="none" width={40} />
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            indeterminate={
                              selectedDocumentRefIds.length > 0 &&
                              selectedDocumentRefIds.length <
                              renderBulkDocumentByPage(
                                bulkDocuments,
                                pagination
                              ).length
                            }
                            checked={
                              selectedDocumentRefIds.length > 0 &&
                              selectedDocumentRefIds.length ===
                              renderBulkDocumentByPage(
                                bulkDocuments,
                                pagination
                              ).length
                            }
                            onChange={(event) =>
                              handleSelectDocument(event.target.checked)
                            }
                          />
                        </TableCell>
                        <TableCell align="right" width={64}>
                          No.
                        </TableCell>
                        <TableCell>{t('bulkPlanning.tableHeaders.documentName')}</TableCell>
                        <TableCell>{t('bulkPlanning.tableHeaders.documentNo')}</TableCell>
                        <TableCell>{t('bulkPlanning.tableHeaders.documentLevel')}</TableCell>
                        <TableCell className={classes.creatorField}>
                          {t('bulkPlanning.tableHeaders.documentCreator')}
                          <div>
                            <Badge
                              color="secondary"
                              variant="dot"
                              overlap="circle"
                              invisible={
                                Boolean(anchorElement) || !isShowAllUsers
                              }
                            >
                              <IconButton
                                size="small"
                                edge="end"
                                onClick={handleAnchorElement(true)}
                              >
                                <MoreVertRounded />
                              </IconButton>
                            </Badge>
                          </div>
                          <Menu
                            anchorEl={anchorElement}
                            keepMounted
                            getContentAnchorEl={null}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'right'
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'right'
                            }}
                            open={Boolean(anchorElement)}
                            onClose={handleAnchorElement(false)}
                          >
                            <FormControlLabel
                              className={classes.marginFormControlLabel}
                              control={
                                <Checkbox
                                  checked={isShowAllUsers}
                                  onChange={handleChangeRelatedUsers}
                                />
                              }
                              label={t('bulkPlanning.showAllUsers') as string}
                            />
                          </Menu>
                        </TableCell>
                        <TableCell width={40} align="center" padding="none">
                          {selectedDocumentRefIds.length > 0 && (
                            <Tooltip title={t('bulkPlanning.delete') as string} arrow>
                              <IconButton
                                size="small"
                                onClick={handleOpenDeleteAllDialog}
                              >
                                <DeleteRounded />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {renderBulkDocumentByPage(bulkDocuments, pagination).map(
                        (document, index) => {
                          const accessControl = getValue(
                            document.accessControlId,
                            accessControls
                          );
                          const documentTemplate = getValue(
                            document.documentId,
                            documents
                          );
                          const open = getStatus(
                            document.refId,
                            openedDocumentRefIds
                          );
                          const showError =
                            !open &&
                            document.projectDocumentRevisions.some(
                              (documentRevision) =>
                                !documentRevision.purposeId ||
                                !documentRevision.revisionId ||
                                !documentRevision.manHour ||
                                !documentRevision.planIssueDate ||
                                !documentRevision.planCompletedDate
                            );
                          const selectedRow = getStatus(
                            document.refId,
                            selectedDocumentRefIds
                          );
                          const selected = showError || selectedRow;
                          const canUndo =
                            selectedDocumentRefIds.length === 0 &&
                            historyDeleteDocumentRevisions.filter(
                              ({ refDocumentId }) =>
                                refDocumentId === document.refId
                            ).length > 0;
                          const { error, helperText } =
                            checkDocumentNo(document);

                          return (
                            <React.Fragment key={index}>
                              <TableRow
                                classes={{
                                  root: clsx(classes.unsetAll, {
                                    [selectedRow
                                      ? classes.tableRowRoot
                                      : classes.tableRowRootError]: selected
                                  }),
                                  selected: clsx({
                                    [selectedRow
                                      ? classes.tableRowSelected
                                      : classes.tableRowSelectedError]: selected
                                  })
                                }}
                                selected={selected}
                                hover
                              >
                                <TableCell align="center" padding="none">
                                  <Tooltip title={(open ? t('bulkPlanning.common.collapse') : t('bulkPlanning.common.expand')) as string}>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleOpenDocument(document.refId)
                                      }
                                    >
                                      {open ? (
                                        <KeyboardArrowDownRounded />
                                      ) : (
                                        <KeyboardArrowRightRounded />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="center" padding="checkbox">
                                  <Checkbox
                                    color="primary"
                                    checked={selectedRow}
                                    onClick={() =>
                                      handleSelectDocument(
                                        !selectedRow,
                                        document.refId
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  {pagination.page * pagination.limit +
                                    index +
                                    1}
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    className={clsx({
                                      [classes.errorInputUnderline]:
                                        !document.name
                                    })}
                                    id="document-name"
                                    name="name"
                                    multiline
                                    fullWidth
                                    helperText={
                                      documentTemplate &&
                                      documentTemplate.name !== document.name &&
                                      `From: ${documentTemplate.name ?? ''}`
                                    }
                                    value={document.name}
                                    onChange={(event) =>
                                      handleChangeDocumentDetail(
                                        event.target.value,
                                        'name',
                                        document.refId
                                      )
                                    }
                                    required
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    className={clsx({
                                      [classes.errorInputUnderline]:
                                        !document.no
                                    })}
                                    id="document-no"
                                    name="no"
                                    multiline
                                    fullWidth
                                    value={document.no}
                                    onChange={(event) =>
                                      handleChangeDocumentDetail(
                                        event.target.value,
                                        'no',
                                        document.refId
                                      )
                                    }
                                    required
                                    error={error}
                                    helperText={helperText}
                                  />
                                </TableCell>
                                <TableCell className={classes.notClick}>
                                  <Typography color="textSecondary">
                                    {accessControl?.name ?? ''}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Autocomplete
                                    className={clsx({
                                      [classes.errorInputUnderline]:
                                        !document.documentCreatorId
                                    })}
                                    id="document-creator"
                                    options={getRelatedUser(
                                      document.userIds,
                                      users
                                    )}
                                    getOptionLabel={(option) =>
                                      `${option.firstName} ${option.lastName}`
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        required
                                      />
                                    )}
                                    value={getValue(
                                      document.documentCreatorId,
                                      users
                                    )}
                                    onChange={(event, newValue) =>
                                      handleChangeDocumentDetail(
                                        newValue?.id ?? null,
                                        'documentCreatorId',
                                        document.refId
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell
                                  width={64}
                                  align="center"
                                  padding="none"
                                >
                                  {selectedDocumentRefIds.length === 0 && (
                                    <div className={classes.actionField}>
                                      <Tooltip title={t('bulkPlanning.duplicate') as string} arrow>
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleDuplicateDocument(document)
                                          }
                                        >
                                          <ContentCopyOutlined />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title={t('bulkPlanning.delete') as string} arrow>
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleOpenDeleteOneDialog(document)
                                          }
                                        >
                                          <DeleteRounded />
                                        </IconButton>
                                      </Tooltip>
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                              <TableRow
                                classes={{
                                  root: clsx(classes.unsetAll, {
                                    [selectedRow
                                      ? classes.tableRowRoot
                                      : classes.tableRowRootError]: selected
                                  }),
                                  selected: clsx({
                                    [selectedRow
                                      ? classes.tableRowSelected
                                      : classes.tableRowSelectedError]: selected
                                  })
                                }}
                                selected={selected}
                              >
                                {open && (
                                  <TableCell padding="none" colSpan={8}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableCell>{t('bulkPlanning.tableHeaders.purpose')}</TableCell>
                                        <TableCell>{t('bulkPlanning.tableHeaders.revision')}</TableCell>
                                        <TableCell
                                          align="center"
                                          padding="checkbox"
                                        >
                                          {t('projectDocument.autoApproval')}
                                        </TableCell>
                                        <TableCell width={120}>
                                          {t('bulkPlanning.tableHeaders.manHour')}
                                        </TableCell>
                                        <TableCell>{t('bulkPlanning.tableHeaders.issueDate')}</TableCell>
                                        <TableCell>
                                          {t('bulkPlanning.tableHeaders.completedDate')}
                                          <HelpTooltip>
                                            {t('projectDocument.autoCalculateDate', { days: DAYS_FOR_COMPLETE_REVIEW })}
                                          </HelpTooltip>
                                        </TableCell>
                                        <TableCell
                                          width={40}
                                          align="center"
                                          padding="none"
                                        >
                                          {canUndo && (
                                            <Tooltip title={t('bulkPlanning.undo') as string} arrow>
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  onUndoDocumentRevision(
                                                    document
                                                  )
                                                }
                                              >
                                                <UndoRounded />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                        </TableCell>
                                      </TableHead>
                                      <TableBody>
                                        {document.projectDocumentRevisions.map(
                                          (
                                            documentRevision,
                                            revisionIndex,
                                            documentRevisions
                                          ) => {
                                            const purpose = getValue(
                                              documentRevision.purposeId,
                                              purposes
                                            );
                                            const revisionList =
                                              purpose?.revisions ?? [];
                                            const revision = getValue(
                                              documentRevision.revisionId,
                                              revisionList
                                            );

                                            return (
                                              <TableRow
                                                className={classes.unsetAll}
                                                hover
                                                key={revisionIndex}
                                              >
                                                <TableCell>
                                                  <Autocomplete
                                                    className={clsx(
                                                      classes.flexGrow2,
                                                      {
                                                        [classes.errorInputUnderline]:
                                                          !purpose
                                                      }
                                                    )}
                                                    id="purpose"
                                                    options={purposes}
                                                    getOptionLabel={(option) =>
                                                      `${option.abbreviation}: ${option.fullName}`
                                                    }
                                                    renderInput={(params) => (
                                                      <TextField
                                                        {...params}
                                                        size="small"
                                                        required
                                                      />
                                                    )}
                                                    value={purpose}
                                                    onChange={(
                                                      event,
                                                      newValue
                                                    ) =>
                                                      handleChangeDocumentRevisionDetail(
                                                        newValue?.id ?? null,
                                                        'purposeId',
                                                        document.refId,
                                                        revisionIndex
                                                      )
                                                    }
                                                  />
                                                </TableCell>
                                                <TableCell width={128}>
                                                  <Autocomplete
                                                    className={clsx({
                                                      [classes.errorInputUnderline]:
                                                        !revision
                                                    })}
                                                    id="revision"
                                                    options={revisionList}
                                                    getOptionLabel={(option) =>
                                                      option.name
                                                    }
                                                    renderInput={(params) => (
                                                      <TextField
                                                        {...params}
                                                        size="small"
                                                        required
                                                      />
                                                    )}
                                                    value={revision}
                                                    onChange={(
                                                      event,
                                                      newValue
                                                    ) =>
                                                      handleChangeDocumentRevisionDetail(
                                                        newValue?.id ?? null,
                                                        'revisionId',
                                                        document.refId,
                                                        revisionIndex
                                                      )
                                                    }
                                                  />
                                                </TableCell>
                                                <TableCell
                                                  className={clsx({
                                                    [classes.notClick]:
                                                      documentRevision.purposeId !==
                                                      PurposeLabel.IFR
                                                  })}
                                                  align="center"
                                                  padding="checkbox"
                                                >
                                                  {documentRevision.purposeId ===
                                                    PurposeLabel.IFR && (
                                                      <Checkbox
                                                        checked={
                                                          documentRevision.isAutoApproval
                                                        }
                                                        color="primary"
                                                        disabled={
                                                          documentRevision.purposeId !==
                                                          PurposeLabel.IFR
                                                        }
                                                        onChange={(event) =>
                                                          handleChangeDocumentRevisionDetail(
                                                            event.target.checked,
                                                            'isAutoApproval',
                                                            document.refId,
                                                            revisionIndex
                                                          )
                                                        }
                                                      />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                  <TextField
                                                    className={clsx({
                                                      [classes.errorInputUnderline]:
                                                        !documentRevision.manHour
                                                    })}
                                                    type="number"
                                                    size="small"
                                                    value={
                                                      documentRevision.manHour
                                                        ? documentRevision.manHour
                                                        : ''
                                                    }
                                                    onChange={(event) => {
                                                      const parsedValue =
                                                        Number.parseFloat(
                                                          event.target.value
                                                        );
                                                      const value = isNaN(
                                                        parsedValue
                                                      )
                                                        ? null
                                                        : parsedValue;
                                                      handleChangeDocumentRevisionDetail(
                                                        value,
                                                        'manHour',
                                                        document.refId,
                                                        revisionIndex,
                                                        { positiveNumber: true }
                                                      );
                                                    }}
                                                    required
                                                  />
                                                </TableCell>
                                                <TableCell width={200}>
                                                  <KeyboardDatePicker
                                                    className={clsx({
                                                      [classes.errorInputUnderline]:
                                                        !documentRevision.planIssueDate
                                                    })}
                                                    autoOk
                                                    size="small"
                                                    variant="inline"
                                                    InputAdornmentProps={{
                                                      position: 'end'
                                                    }}
                                                    value={
                                                      documentRevision.planIssueDate
                                                    }
                                                    onChange={(event) =>
                                                      handleChangeDocumentRevisionDetail(
                                                        event
                                                          ? moment(event)
                                                            .endOf('day')
                                                            .toDate()
                                                          : null,
                                                        'planIssueDate',
                                                        document.refId,
                                                        revisionIndex
                                                      )
                                                    }
                                                    KeyboardButtonProps={{
                                                      'aria-label':
                                                        'change plan issue date'
                                                    }}
                                                    required
                                                  />
                                                </TableCell>
                                                <TableCell width={200}>
                                                  <KeyboardDatePicker
                                                    className={clsx({
                                                      [classes.errorInputUnderline]:
                                                        !documentRevision.planIssueDate
                                                    })}
                                                    autoOk
                                                    size="small"
                                                    variant="inline"
                                                    InputAdornmentProps={{
                                                      position: 'end'
                                                    }}
                                                    minDate={
                                                      documentRevision.planIssueDate
                                                    }
                                                    minDateMessage={t('bulkPlanning.common.dateValidation')}
                                                    value={
                                                      documentRevision.planCompletedDate
                                                    }
                                                    onChange={(event) =>
                                                      handleChangeDocumentRevisionDetail(
                                                        event
                                                          ? moment(event)
                                                            .endOf('day')
                                                            .toDate()
                                                          : null,
                                                        'planCompletedDate',
                                                        document.refId,
                                                        revisionIndex
                                                      )
                                                    }
                                                    KeyboardButtonProps={{
                                                      'aria-label':
                                                        'change complete issue date'
                                                    }}
                                                    required
                                                  />
                                                </TableCell>
                                                <TableCell
                                                  width={40}
                                                  align="center"
                                                  padding="none"
                                                >
                                                  {selectedDocumentRefIds.length ===
                                                    0 && (
                                                      <Tooltip
                                                        title={
                                                          documentRevisions.length <=
                                                            1
                                                            ? (t('bulkPlanning.common.cannotDelete') as string) : (t('bulkPlanning.actions.delete') as string)
                                                        }
                                                        arrow
                                                      >
                                                        <span>
                                                          <IconButton
                                                            size="small"
                                                            disabled={
                                                              documentRevisions.length <=
                                                              1
                                                            }
                                                            onClick={() =>
                                                              onDeleteDocumentRevision(
                                                                document,
                                                                documentRevision
                                                              )
                                                            }
                                                          >
                                                            <DeleteRounded />
                                                          </IconButton>
                                                        </span>
                                                      </Tooltip>
                                                    )}
                                                </TableCell>
                                              </TableRow>
                                            );
                                          }
                                        )}
                                      </TableBody>
                                    </Table>
                                  </TableCell>
                                )}
                              </TableRow>
                            </React.Fragment>
                          );
                        }
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <div className={clsx(classes.pagination, classes.mt1)}>
                  <Pagination
                    count={Math.ceil(bulkDocuments.length / pagination.limit)}
                    shape="rounded"
                    page={pagination.page + 1}
                    onChange={handleChangePage}
                  />
                </div>
              </>
            ) : templateDocuments.length === 0 ? (
              <div className={classes.notFoundError}>
                <Typography gutterBottom>{t('bulkPlanning.oops')}</Typography>
                <Typography component="div" variant="caption">
                  {t('bulkPlanning.common.oopsCannotDisplay')}
                </Typography>
                <Typography component="div" variant="caption" gutterBottom>
                  {t('bulkPlanning.common.checkMasterData')}
                </Typography>
              </div>
            ) : (
              <div className={classes.notFoundError}>
                <Typography gutterBottom>{t('bulkPlanning.omg')}</Typography>
                <Typography component="div" variant="caption">
                  {t('bulkPlanning.common.omgDeletedAll')}
                </Typography>
                <Typography component="div" variant="caption" gutterBottom>
                  {t('bulkPlanning.common.clickResetDefault')}
                </Typography>
                <Button
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={onReset}
                >
                  {t('bulkPlanning.actions.reset')}
                </Button>
              </div>
            )}
          </Collapse>
        </Paper>
        <div className={classes.actionButton}>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            startIcon={<KeyboardArrowLeftRounded />}
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            {t('bulkPlanning.actions.back')}
          </Button>
          {activeStep < stepKeys.length - 1 ? (
            <Button
              variant="contained"
              color="primary"
              size="large"
              endIcon={<KeyboardArrowRightRounded />}
              disabled={nextButton(activeStep).disabled}
              onClick={nextButton(activeStep).action}
            >
              {t('bulkPlanning.actions.next')}
            </Button>
          ) : (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={nextButton(activeStep).disabled}
            >
              {t('bulkPlanning.actions.createDocuments')}
            </Button>
          )}
        </div>
      </form>
      <ConfirmDeleteBox
        open={openDeletePipingListDialog}
        text={selectedPipingList?.name ?? (t('bulkPlanning.piping.untitledPipingList') as string)}
        onDelete={onDeletePipingList}
        onClose={handleOpenDeletePipingListDialog}
      >
        <Typography color="textSecondary">
          {t('bulkPlanning.piping.messages.documentsNotAffected')}
        </Typography>
      </ConfirmDeleteBox>
      <ConfirmDeleteBox
        open={openDeleteOneDialog}
        text={selectedDocument?.name ?? t('bulkPlanning.common.untitledDocument') as string}
        onDelete={onDeleteDocument}
        onClose={handleOpenDeleteOneDialog}
      />
      <ConfirmDeleteBox
        open={openDeleteAllDialog}
        text={`${selectedDocumentRefIds.length} document${selectedDocumentRefIds.length > 1 ? 's' : ''
          }`}
        onDelete={onDeleteSelectedDocuments}
        onClose={handleOpenDeleteAllDialog}
      >
        <ul className={classes.ul}>
          {selectedDocumentRefIds.map((refId) => {
            const document = bulkDocuments.find(
              (document) => document.refId === refId
            );
            return <li key={refId}>{document?.name ?? t('bulkPlanning.common.untitledDocument')}</li>;
          })}
        </ul>
      </ConfirmDeleteBox>
    </div>
  );
}

const StyledBadge = withStyles((theme: Theme) =>
  createStyles({
    badge: {
      right: 4,
      top: 20,
      border: `2px solid ${theme.palette.background.paper}`
    }
  })
)(Badge);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: { margin: theme.spacing(1.5, 0, 12, 0) },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    paperSearch: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1, 2, 1, 1),
      borderRadius: 18,
      width: 240,
      height: 36,
      zIndex: 2
    },
    searchInput: {
      marginLeft: theme.spacing(1)
    },
    colorTextSecondary: {
      color: theme.palette.text.secondary
    },
    clickable: { cursor: 'pointer' },
    notClick: { cursor: 'not-allowed' },
    unsetAll: {
      '& > *': {
        borderBottom: 'unset'
      }
    },
    unset: { borderBottom: 'unset' },
    tableRowRoot: {
      '&$tableRowSelected, &$tableRowSelected:hover': {
        backgroundColor: fade(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        )
      }
    },
    tableRowSelected: {
      backgroundColor: fade(
        theme.palette.primary.main,
        theme.palette.action.selectedOpacity
      )
    },
    tableRowRootError: {
      '&$tableRowSelected, &$tableRowSelected:hover': {
        backgroundColor: fade(
          theme.palette.secondary.main,
          theme.palette.action.selectedOpacity
        )
      }
    },
    tableRowSelectedError: {
      backgroundColor: fade(
        theme.palette.secondary.main,
        theme.palette.action.selectedOpacity
      )
    },
    errorInputUnderline: {
      '& .MuiInput-underline:before, & .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInput-underline:after':
      {
        borderBottomColor: theme.palette.error.main,
        borderBottomWidth: 2
      }
    },
    creatorField: {
      padding: theme.spacing(0.75, 2),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    actionField: {
      display: 'flex',
      alignItems: 'center'
    },
    revisionContainer: {
      display: 'flex',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
      marginBottom: theme.spacing(1)
    },
    flexSpace: {
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center'
    },
    truncatedChip: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%' // Adjust this to control the maximum width of the Chip
    },
    notFoundError: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      padding: theme.spacing(1, 2)
    },
    fileNotFound: {
      padding: theme.spacing(2, 1),
      textAlign: 'center'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end'
    },
    marginFormControlLabel: { margin: theme.spacing(0, 1.5, 0, 0) },
    ul: { paddingLeft: theme.spacing(2) },
    actionButton: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    flexGrow: { flexGrow: 1 },
    flexGrow2: { flexGrow: 2 },
    mt1: { marginTop: theme.spacing(1) },
    my1: { margin: theme.spacing(1, 0) },
    my2: { margin: theme.spacing(2, 0) },
    px1: { padding: theme.spacing(0, 1) }
  })
);
